import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage, CONTACTS } from "../i18n";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col items-center justify-center overflow-hidden grain"
      style={{ backgroundColor: "#480A1A" }}
    >
      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 flex flex-col items-center text-center px-6 pt-24 pb-16"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="w-[min(74vw,420px)] overflow-hidden"
        >
          <img
            src="/logo-wordmark.png"
            alt="Sant'Irene"
            data-testid="hero-logo"
            className="w-full h-auto"
          />
        </motion.div>

        <h1 data-testid="hero-main-title" className="mt-2">
          <MaskedLine
            delay={0.55}
            className="font-display italic font-light text-4xl sm:text-5xl lg:text-6xl text-[#F5EFE6] tracking-tight"
          >
            {t.payoff}
          </MaskedLine>
        </h1>

        <div data-testid="hero-subtitle" className="mt-7 max-w-xl">
          <MaskedLine
            delay={0.8}
            className="font-sans text-sm sm:text-base leading-relaxed text-[#D9C6BB]"
          >
            {t.hero.subtitle}
          </MaskedLine>
          <MaskedLine
            delay={0.95}
            className="mt-2 font-display italic text-sm sm:text-base text-[#C69255]/80"
          >
            {t.hero.subtitle2}
          </MaskedLine>
        </div>

        <motion.div
          data-testid="hero-opening-badge"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-9 flex flex-col items-center gap-2"
        >
          <span className="font-meta text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#C69255]">
            {t.hero.hoursLine1}
          </span>
          <span className="font-meta text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#C69255]">
            {t.hero.hoursLine2}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.45, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-4"
        >
          <a
            data-testid="hero-reserve-cta"
            href={CONTACTS.superbUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-9 py-4 rounded-full bg-[#C69255] text-[#2C0F1A] font-meta text-xs uppercase tracking-[0.25em] hover:bg-[#D9AB72] transition-colors duration-300"
          >
            {t.hero.ctaBook}
          </a>
          <button
            data-testid="hero-explore-menu-cta"
            onClick={() =>
              window.__lenis
                ? window.__lenis.scrollTo("#menu", { offset: -64, duration: 1.6 })
                : document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" })
            }
            className="px-9 py-4 rounded-full border border-[#C69255]/45 text-[#E9DCCF] font-meta text-xs uppercase tracking-[0.25em] hover:border-[#C69255] hover:text-[#C69255] transition-colors duration-300"
          >
            {t.hero.ctaMenu}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="font-meta text-[9px] uppercase tracking-[0.35em] text-[#E9DCCF]/50">
          {t.hero.scroll}
        </span>
        <motion.span
          animate={{ y: [0, 7, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="block w-px h-9 bg-[#C69255]/60"
        />
      </motion.div>
    </section>
  );
};
